/**
 * integrations-registry.js - THE single source of truth for the integration
 * roster. Pure data, zero imports, importable from plain node - which is what
 * lets scripts/export-integrations.mjs (repo root) generate the website
 * surfaces from this exact file:
 *   - machfivemagnet_hugo/data/integrations.yaml   (/integrations/ page cards)
 *   - machfivemagnet_hugo/content/docs/integrations/<slug>.md  (one docs page
 *     per integration, titled "Mach Five Magnet and <Name>", steps mirroring
 *     the in-app setup guide because they ARE the in-app setup guide)
 *   - the docs sidebar menu block in machfivemagnet_hugo/hugo.toml
 *
 * ADDING AN INTEGRATION: add the provider entry here (category, card, crm?,
 * walkthrough, web), drop the logo SVG in public/integrations/, then run
 * `node scripts/export-integrations.mjs` and commit BOTH repos. The client
 * test suite fails if an entry is missing web copy or walkthrough steps.
 */

export const WALKTHROUGHS = {
  google_ads: {
    what: 'See ad spend lined up next to the leads it generated, so you know which campaigns are actually working.',
    steps: [
      'Click Connect and sign in with a Google account that has access to your Ads accounts.',
      'Choose which ad accounts should feed reporting.',
      'Metrics refresh automatically once a day. Click Sync now any time for an immediate pull.',
    ],
  },
  calendly: {
    what: 'Let visitors book directly from a magnet conversation, and get notified the moment they do.',
    steps: [
      'Click Connect and authorize your Calendly account.',
      'Turn on Calendly booking for any magnet’s Book action, under Magnets.',
      'Confirmed and canceled bookings sync back to the lead automatically. Webhooks need a paid Calendly plan; a free plan still lets visitors book, just without the automatic lead sync.',
    ],
  },
  callrail: {
    what: 'Every tracked phone call becomes a lead automatically, complete with recording and source.',
    steps: [
      'In CallRail, go to Integrations, then Data access, then API keys, and create a key. Read-only works for calls; Allow Writes lets us set up call sync automatically.',
      'Paste the key here and pick the account and company this workspace tracks.',
      'Calls start landing in your Leads inbox within minutes.',
    ],
  },
  google_analytics: {
    what: 'See how visitors reach your site and behave once they get there. Sessions, pageviews, and engagement, right alongside the leads your magnets capture.',
    steps: [
      'Click Connect and sign in with a Google account that has access to your GA4 property.',
      'Choose which GA4 properties should feed reporting.',
      'See traffic beside your magnet leads under Analytics, then Traffic.',
      'Metrics refresh automatically once a day. Click Sync now any time for an immediate pull.',
    ],
  },
  hubspot: {
    what: 'Every captured lead becomes a HubSpot contact automatically, with the magnet conversation on its timeline.',
    steps: [
      'Click Connect and sign in to the HubSpot account this workspace should sync into.',
      'New leads sync as contacts within minutes. Existing contacts are updated, never duplicated.',
      'Optional: turn on qualified-lead sync to set the contact lifecycle stage when you qualify a lead.',
    ],
  },
  zoho: {
    what: 'Every captured lead lands in the Zoho CRM Leads module automatically, with the magnet conversation as a note.',
    steps: [
      'Click Connect and sign in to the Zoho account this workspace should sync into.',
      'New leads sync within minutes. Existing Zoho leads are updated by email, never duplicated.',
      'Optional: turn on qualified-lead sync to set the Zoho lead status when you qualify a lead.',
    ],
  },
  salesforce: {
    what: 'Every captured lead becomes a Salesforce lead automatically, with the magnet conversation as a note on the record.',
    steps: [
      'Click Connect and sign in to the Salesforce org this workspace should sync into.',
      'New leads sync within minutes. Existing Salesforce leads are updated by email, never duplicated.',
      'Qualified-lead sync sets the Salesforce lead status. If your org uses custom status values, set yours on this page.',
    ],
  },
  ms_bookings: {
    what: 'Visitors pick a real open time and book inside the magnet, straight onto your Microsoft Bookings calendar. Each booking is added to the lead, with the Teams meeting link included.',
    steps: [
      'Click Connect and sign in with your Microsoft 365 work account.',
      'Approve in-magnet booking on the Microsoft screen that follows. One-time, one click for a Microsoft 365 admin. It lets visitors book inside the magnet instead of on your Microsoft page.',
      'Set any magnet Book button to a Bookings page, under Magnets.',
      'Bookings sync to your leads every few minutes. Click Sync now for an immediate sync.',
    ],
    // Screenshots on the website docs page (export script). Files live in
    // machfivemagnet_hugo/static/images/docs/integrations/ — the export
    // hard-fails if one is missing. section: intro | setup | editor.
    shots: [
      { section: 'intro', file: 'ms_bookings-scheduler.png', alt: 'A magnet showing real open times from Microsoft Bookings', caption: 'A visitor picks a real open time inside the magnet.' },
      { section: 'setup', file: 'ms_bookings-steps.png', alt: 'The Microsoft Bookings integration page showing the three setup steps', caption: 'The integration page walks the setup, step by step.' },
      { section: 'editor', file: 'ms_bookings-editor.png', alt: 'The magnet editor Book call-to-action set to a Microsoft Bookings page and service', caption: 'The Book call-to-action, set to a Bookings page and service.' },
    ],
    // Rendered as progressive disclosure in the app's in-magnet booking
    // stepper AND as its own section on the website docs page (export script).
    editor: {
      title: 'Turn it on in the magnet editor',
      steps: [
        'Under Magnets, edit a magnet and open its Welcome and CTAs tab.',
        'In the Call-to-action bar, check Book.',
        'Set the Book source to Microsoft Bookings.',
        'Pick your Bookings page, then pick a service. A service gives visitors the in-magnet scheduler with your real open times. Whole page opens your Microsoft page inside the magnet instead.',
        'Save. Visitors who tap Book now pick a time and book without leaving the magnet, and the appointment lands on your Bookings calendar with a Teams link.',
      ],
    },
  },
  gohighlevel: {
    what: 'Every captured lead becomes a HighLevel contact automatically, with the conversation as a note and a tag your workflows can use.',
    steps: [
      'In HighLevel, open the sub-account, go to Settings, then Private Integrations, and create a token with the contacts.write scope.',
      'Copy the sub-account Location ID from Settings, then Business Profile.',
      'Paste both here. Qualified leads get a tag your automations can trigger on.',
    ],
  },
  calcom: {
    what: 'Visitors book your Cal.com events from a magnet. Each booking is added to the lead. Works on every Cal.com plan, including free.',
    steps: [
      'In Cal.com, go to Settings, then Developer, then API keys, and create a key.',
      'Paste the key here. Booking sync is set up automatically.',
      'Turn on Cal.com booking for any magnet, in the Book call-to-action under Magnets.',
    ],
  },
};

// ── provider descriptors ─────────────────────────────────────────────────────
// Everything that differs between providers lives here as data; the logic in
// this module exists exactly once. `connect` present = OAuth provider;
// `picker` present = has sub-accounts to choose; CallRail's pasted-key flow is
// its own section. `urlSlug` is the hash-route segment; `logo` the SVG under
// public/integrations/ (RELATIVE src - vite base is './'; a missing file falls
// back to the Material glyph via onerror).
export const PROVIDERS = {
  google_ads: {
    slug: 'google_ads',
    web: { copy: 'Spend, clicks, and conversions show next to the leads they produced. Captured and qualified leads are sent back to Google Ads as conversions, so bidding optimizes toward real leads.' },
    urlSlug: 'google-ads',
    category: 'advertising',
    label: 'Google Ads',
    logo: 'integrations/google-ads.svg',
    connect: { fallbackError: 'Could not start the Google connection.' },
    disconnect: {
      confirm: 'Daily metric refreshes stop until reconnected.',
      toast: 'Google Ads disconnected.',
    },
    card: {
      tint: 'primary',
      icon: 'ads_click',
      sub: 'Spend and clicks beside your leads.',
      tile: 'See ad spend and cost per lead.',
      ids: { connect: 'intg-g-connect', choose: 'intg-g-accounts', sync: 'intg-g-sync', disconnect: 'intg-g-disconnect', panel: 'intg-g-picker' },
      chooseLabel: 'Choose accounts',
      idleDesc: 'Connect a Google account with access to your ad accounts. Spend and clicks show next to your magnet leads in Ad performance.',
      brokenDesc: 'The connection stopped working. Reconnect to resume daily metric refreshes.',
      reachDesc: (n) => `This connection can reach <b>${n}</b> ad account${n === 1 ? '' : 's'}. Choose which ones feed reporting; metrics refresh daily in Ad performance.`,
      noneDesc: 'Connected, but no ad accounts are reachable from this Google account. Reconnect with an account that has Google Ads access.',
      reachableCount: (acct) => acct?.customer_ids?.length || 0,
    },
    picker: {
      path: 'accounts',
      itemsField: 'accounts',
      saveField: 'customer_ids',
      dataAttr: 'intg-acct',
      datasetKey: 'intgAcct',
      ids: { cancel: 'intg-picker-cancel', save: 'intg-picker-save' },
      loading: 'Loading your ad accounts…',
      loadError: 'Could not load your ad accounts. Try again, or reconnect if this keeps happening.',
      heading: 'Accounts that feed reporting',
      emptyMsg: 'No ad accounts are reachable from this Google account.',
      namePrefix: 'Account ',
      rowDisabled: (a) => !!(a.manager || a.error),
      // Children found through an MCC carry via_manager and Google's own status.
      // A suspended child (typically pending advertiser verification) stays
      // LINKABLE - metrics and provisioning start once Google enables it - but
      // the badge says so instead of letting the row look ready.
      rowBadge: (a) => (a.manager ? 'Manager'
        : a.error ? 'Unavailable'
        : (a.status && a.status !== 'ENABLED') ? (a.status[0] + a.status.slice(1).toLowerCase())
        : a.via_manager ? 'Via MCC'
        : (a.currency || '')),
      savedToast: (n) => `${n} account${n === 1 ? '' : 's'} linked. First sync is running.`,
      saveError: 'Could not save account selection.',
    },
    sync: {
      itemsField: 'accounts',
      failedToast: (n) => `Sync finished with ${n} account error${n === 1 ? '' : 's'}.`,
    },
  },

  google_analytics: {
    slug: 'google_analytics',
    web: { copy: 'Traffic and engagement from your GA4 property show next to your magnet leads.' },
    urlSlug: 'google-analytics',
    category: 'analytics',
    label: 'Google Analytics',
    logo: 'integrations/google-analytics.svg',
    connect: { fallbackError: 'Could not start the Google Analytics connection.' },
    disconnect: {
      confirm: 'Daily metric refreshes stop until reconnected.',
      toast: 'Google Analytics disconnected.',
    },
    card: {
      tint: 'success',
      icon: 'monitoring',
      sub: 'Traffic and engagement beside your leads.',
      tile: 'See site traffic and engagement.',
      ids: { connect: 'intg-a-connect', choose: 'intg-a-properties', sync: 'intg-a-sync', disconnect: 'intg-a-disconnect', panel: 'intg-a-picker' },
      chooseLabel: 'Choose properties',
      idleDesc: 'Connect a Google account with access to your GA4 property. Traffic and engagement show next to your magnet leads.',
      brokenDesc: 'The connection stopped working. Reconnect to resume daily metric refreshes.',
      reachDesc: (n) => `This connection can reach <b>${n}</b> GA4 propert${n === 1 ? 'y' : 'ies'}. Choose which ones feed reporting; metrics refresh daily.`,
      noneDesc: 'Connected, but no GA4 properties are reachable from this Google account. Reconnect with an account that has Analytics access.',
      reachableCount: (acct) => acct?.properties?.length || 0,
    },
    picker: {
      path: 'properties',
      itemsField: 'properties',
      saveField: 'property_ids',
      dataAttr: 'intg-prop',
      datasetKey: 'intgProp',
      ids: { cancel: 'intg-a-picker-cancel', save: 'intg-a-picker-save' },
      loading: 'Loading your GA4 properties…',
      loadError: 'Could not load your GA4 properties. Try again, or reconnect if this keeps happening.',
      heading: 'Properties that feed reporting',
      emptyMsg: 'No GA4 properties are reachable from this Google account.',
      namePrefix: 'Property ',
      rowDisabled: () => false,
      rowBadge: (p) => p.account || '',
      savedToast: (n) => `${n} propert${n === 1 ? 'y' : 'ies'} linked. First sync is running.`,
      saveError: 'Could not save property selection.',
    },
    sync: {
      itemsField: 'properties',
      failedToast: (n) => `Sync finished with ${n} property error${n === 1 ? '' : 's'}.`,
    },
  },

  calendly: {
    slug: 'calendly',
    web: { copy: 'Visitors book your Calendly events from the magnet Book button. Confirmed, rescheduled, and canceled bookings update the lead.' },
    urlSlug: 'calendly',
    category: 'scheduling',
    label: 'Calendly',
    logo: 'integrations/calendly.svg',
    connect: { fallbackError: 'Could not start the Calendly connection.' },
    disconnect: {
      confirm: 'Magnet booking falls back to the built-in scheduler and booking sync stops.',
      toast: 'Calendly disconnected.',
    },
    card: {
      tint: 'accent',
      icon: 'event',
      sub: 'Bookings on your magnets.',
      tile: 'Visitors book your Calendly events.',
      ids: { connect: 'intg-c-connect', disconnect: 'intg-c-disconnect' },
      idleDesc: 'Visitors book your Calendly events from the magnet Book button. Each booking is added to the lead.',
      brokenDesc: 'The connection stopped working. Reconnect to resume booking sync.',
    },
  },

  hubspot: {
    slug: 'hubspot',
    web: { copy: 'Each captured lead becomes a HubSpot contact, with the conversation saved as a note. Qualified leads update the lifecycle stage.' },
    urlSlug: 'hubspot',
    category: 'crm',
    label: 'HubSpot',
    logo: 'integrations/hubspot.svg',
    connect: { fallbackError: 'Could not start the HubSpot connection.' },
    disconnect: {
      confirm: 'New leads stop syncing to HubSpot until reconnected.',
      toast: 'HubSpot disconnected.',
    },
    card: {
      tint: 'accent',
      icon: 'hub',
      sub: 'Leads into your CRM.',
      tile: 'Leads sync to HubSpot as contacts.',
      ids: { connect: 'intg-h-connect', sync: 'intg-h-sync', disconnect: 'intg-h-disconnect' },
      idleDesc: 'Each captured lead becomes a HubSpot contact, with the conversation saved as a note. Qualified leads update the lifecycle stage.',
      brokenDesc: 'The connection stopped working. Reconnect to resume lead sync.',
    },
    // Present = CRM destination: the detail page renders the shared Lead sync
    // section (ledger counts + qualified opt-in + Sync now) from this copy.
    crm: {
      syncDesc: 'Captured leads become contacts, with the conversation as a note on the timeline. Sync runs every few minutes.',
      qualifiedToggle: 'Set the lifecycle stage when a lead is qualified',
      countLabels: { contact: 'Contacts', qualified: 'Qualified leads' },
    },
  },

  salesforce: {
    slug: 'salesforce',
    web: { copy: 'Each captured lead becomes a Salesforce lead, with the conversation saved as a note. Qualified leads update the Salesforce lead status.' },
    urlSlug: 'salesforce',
    category: 'crm',
    label: 'Salesforce',
    logo: 'integrations/salesforce.svg',
    connect: { fallbackError: 'Could not start the Salesforce connection.' },
    disconnect: {
      confirm: 'New leads stop syncing to Salesforce until reconnected.',
      toast: 'Salesforce disconnected.',
    },
    card: {
      tint: 'primary',
      icon: 'cloud_sync',
      sub: 'Leads into your CRM.',
      tile: 'Leads sync to Salesforce.',
      ids: { connect: 'intg-sf-connect', sync: 'intg-sf-sync', disconnect: 'intg-sf-disconnect' },
      idleDesc: 'Each captured lead becomes a Salesforce lead, with the conversation saved as a note. Qualified leads update the Salesforce lead status.',
      brokenDesc: 'The connection stopped working. Reconnect to resume lead sync.',
    },
    crm: {
      syncDesc: 'Captured leads become Salesforce leads, with the conversation as a note. Sync runs every few minutes.',
      qualifiedToggle: 'Set the Salesforce lead status when a lead is qualified',
      countLabels: { contact: 'Leads', qualified: 'Qualified leads' },
    },
  },

  zoho: {
    slug: 'zoho',
    web: { copy: 'Each captured lead becomes a Zoho CRM lead, with the conversation saved as a note. Qualified leads update the Zoho lead status.' },
    urlSlug: 'zoho',
    category: 'crm',
    label: 'Zoho CRM',
    logo: 'integrations/zoho.svg',
    connect: { fallbackError: 'Could not start the Zoho connection.' },
    disconnect: {
      confirm: 'New leads stop syncing to Zoho CRM until reconnected.',
      toast: 'Zoho CRM disconnected.',
    },
    card: {
      tint: 'primary',
      icon: 'contact_page',
      sub: 'Leads into your CRM.',
      tile: 'Leads sync to the Zoho Leads module.',
      ids: { connect: 'intg-z-connect', sync: 'intg-z-sync', disconnect: 'intg-z-disconnect' },
      idleDesc: 'Each captured lead becomes a Zoho CRM lead, with the conversation saved as a note. Qualified leads update the Zoho lead status.',
      brokenDesc: 'The connection stopped working. Reconnect to resume lead sync.',
    },
    crm: {
      syncDesc: 'Captured leads land in the Zoho Leads module, with the conversation as a note. Sync runs every few minutes.',
      qualifiedToggle: 'Set the Zoho lead status to Qualified when you qualify a lead',
      countLabels: { contact: 'Leads', qualified: 'Qualified leads' },
    },
  },

  ms_bookings: {
    slug: 'ms_bookings',
    web: { copy: 'Visitors pick a real open time and book inside the magnet, no Microsoft page. Each booking is added to the lead, with the Teams meeting link included.' },
    urlSlug: 'microsoft-bookings',
    category: 'scheduling',
    label: 'Microsoft Bookings',
    logo: 'integrations/microsoft-bookings.svg',
    connect: { fallbackError: 'Could not start the Microsoft connection.' },
    disconnect: {
      confirm: 'Bookings stop syncing to your leads until reconnected.',
      toast: 'Microsoft Bookings disconnected.',
    },
    card: {
      tint: 'info',
      icon: 'event_note',
      sub: 'Teams meetings on your magnets.',
      tile: 'Visitors book through your Bookings page.',
      ids: { connect: 'intg-mb-connect', sync: 'intg-mb-sync', disconnect: 'intg-mb-disconnect' },
      idleDesc: 'Connect your Microsoft 365 work account. Each booking is added to the lead, with the Teams meeting link included.',
      brokenDesc: 'The connection stopped working. Reconnect to resume booking sync.',
    },
  },

  gohighlevel: {
    slug: 'gohighlevel',
    web: { copy: 'Each captured lead becomes a HighLevel contact, with the conversation saved as a note. Qualified leads get a tag your automations can use.' },
    urlSlug: 'gohighlevel',
    category: 'crm',
    label: 'HighLevel',
    logo: 'integrations/gohighlevel.svg',
    // No `connect` (OAuth) - pasted private-integration token, two fields.
    disconnect: {
      confirm: 'New leads stop syncing to HighLevel until reconnected.',
      toast: 'HighLevel disconnected.',
    },
    card: {
      tint: 'accent',
      icon: 'device_hub',
      sub: 'Leads into your CRM.',
      tile: 'Leads sync to HighLevel as contacts.',
      ids: { connect: 'intg-gh-connect', sync: 'intg-gh-sync', disconnect: 'intg-gh-disconnect', setup: 'intg-gh-setup' },
      idleDesc: 'Each captured lead becomes a HighLevel contact, with the conversation saved as a note. Qualified leads get a tag your automations can use.',
      brokenDesc: 'The connection stopped working. Reconnect with a current private integration token.',
    },
    crm: {
      syncDesc: 'Captured leads become HighLevel contacts, with the conversation as a note. Qualified leads get a tag your automations can trigger on. Sync runs every few minutes.',
      qualifiedToggle: 'Tag the HighLevel contact when a lead is qualified',
      countLabels: { contact: 'Contacts', qualified: 'Qualified leads' },
    },
  },

  calcom: {
    slug: 'calcom',
    web: { copy: 'Visitors book your Cal.com events from the magnet Book button. Works on every Cal.com plan, including free.' },
    urlSlug: 'calcom',
    category: 'scheduling',
    label: 'Cal.com',
    logo: 'integrations/calcom.svg',
    // No `connect` (OAuth) - pasted-key flow like CallRail's, one field.
    disconnect: {
      confirm: 'Magnet booking falls back to the built-in scheduler and booking sync stops.',
      toast: 'Cal.com disconnected.',
    },
    card: {
      tint: 'success',
      icon: 'event_available',
      sub: 'Bookings on your magnets.',
      tile: 'Visitors book your Cal.com events.',
      ids: { connect: 'intg-cc-connect', disconnect: 'intg-cc-disconnect', setup: 'intg-cc-setup' },
      idleDesc: 'Visitors book your Cal.com events from the magnet Book button. Each booking is added to the lead. Works on every Cal.com plan.',
      brokenDesc: 'The connection stopped working. Reconnect with a current Cal.com API key.',
    },
  },

  callrail: {
    slug: 'callrail',
    web: { copy: 'Tracked calls appear in your leads inbox with the caller, source, and recording. Calls that match an ad click are sent to Google Ads as conversions.' },
    urlSlug: 'callrail',
    category: 'calls',
    label: 'CallRail',
    logo: 'integrations/callrail.svg',
    disconnect: {
      confirm: 'Calls stop landing in your Leads inbox.',
      toast: 'CallRail disconnected.',
    },
    card: {
      tint: 'info',
      icon: 'call',
      sub: 'Calls in your leads inbox.',
      tile: 'Tracked phone calls become leads.',
      ids: { connect: 'intg-r-connect', sync: 'intg-r-sync', disconnect: 'intg-r-disconnect', setup: 'intg-r-setup' },
      idleDesc: 'Paste a CallRail API key. Tracked calls appear in your Leads inbox with the caller, source, and recording.',
      brokenDesc: 'The connection stopped working. Reconnect with a current CallRail API key.',
    },
  },
};

// Hub sections, in display order. Every provider carries a `category`.
export const CATEGORIES = [
  { key: 'crm', title: 'CRM', intro: 'Send captured leads to the CRM your team uses.' },
  { key: 'scheduling', title: 'Scheduling', intro: 'Let visitors book meetings from a magnet.' },
  { key: 'advertising', title: 'Advertising', intro: 'See what your ads produce.' },
  { key: 'analytics', title: 'Analytics', intro: 'See how visitors reach your site.' },
  { key: 'calls', title: 'Call Tracking', intro: 'Turn tracked phone calls into leads.' },
  { key: 'automation', title: 'Automation', intro: 'Send leads anywhere else.' },
];

// The Automation section's tile: the org webhook (which is also how Zapier
// connects). Configured on the Notifications page, so the tile links there.
// The two automation integrations as the WEBSITE and DOCS see them (the app
// hub shows them as one combined tile - AUTOMATION_TILES below).
export const AUTOMATION_INTEGRATIONS = [
  {
    slug: 'slack', name: 'Slack', logo: 'integrations/slack.svg', icon: 'tag',
    web: { copy: 'Every new lead posts to a Slack channel the moment it is captured.' },
    what: 'Post every new lead to a Slack channel the moment it is captured.',
    steps: [
      'In Slack, create an app for your workspace and turn on Incoming Webhooks.',
      'Add a webhook to the channel that should receive leads and copy its URL.',
      'In the app, go to Settings, then Notifications, paste the URL under Slack, and save.',
      'Click Send test. Leads arrive as a formatted card with name, contact, and source.',
    ],
  },
  {
    slug: 'ms-teams', name: 'Microsoft Teams', logo: 'integrations/ms-teams.svg', icon: 'groups',
    web: { copy: 'Every new lead posts to a Teams channel the moment it is captured.' },
    what: 'Post every new lead to a Teams channel the moment it is captured.',
    steps: [
      'In the Teams channel, open Workflows and add "Post to a channel when a webhook request is received".',
      'Copy the workflow URL it gives you.',
      'In the app, go to Settings, then Notifications, paste the URL under Microsoft Teams, and save.',
      'Click Send test. Leads arrive as a card with name, contact, and source.',
    ],
  },
  {
    slug: 'webhook', name: 'Webhook', icon: 'webhook',
    web: { copy: 'Every captured and qualified lead is sent to your own endpoint as signed JSON.' },
    what: 'Send every captured and qualified lead to your own endpoint as signed JSON.',
    steps: [
      'In the app, go to Settings, then Notifications.',
      'Add your endpoint URL and an optional signing secret, then save.',
      'Click Send test to confirm delivery. Leads arrive as JSON with an HMAC signature header.',
    ],
  },
  {
    slug: 'zapier', name: 'Zapier', logo: 'integrations/zapier.svg', icon: 'bolt',
    web: { copy: 'Connect a Zapier zap to the Magnet webhook and send leads to thousands of apps.' },
    what: 'Connect a Zapier zap to the Magnet webhook and send leads to thousands of apps.',
    steps: [
      'In Zapier, create a zap with the Webhooks by Zapier trigger and choose Catch Hook.',
      'Copy the hook URL into the app under Settings, then Notifications, then save.',
      'Click Send test, then map the lead fields in your zap.',
    ],
  },
];

export const AUTOMATION_TILES = [
  {
    key: 'slack', label: 'Slack', logo: 'integrations/slack.svg', icon: 'tag',
    sub: 'Leads in your channel.',
    tile: 'Post every new lead to a Slack channel.',
    href: '#/settings/notifications',
  },
  {
    key: 'ms-teams', label: 'Microsoft Teams', logo: 'integrations/ms-teams.svg', icon: 'groups',
    sub: 'Leads in your channel.',
    tile: 'Post every new lead to a Teams channel.',
    href: '#/settings/notifications',
  },
  {
    key: 'webhook', label: 'Webhook and Zapier', logo: 'integrations/zapier.svg', icon: 'bolt',
    sub: 'Leads to any endpoint.',
    tile: 'Send every lead to Zapier or your own URL.',
    href: '#/settings/notifications',
  },
];

export const WIDE_MARKS = new Set(['salesforce', 'zoho', 'calcom']);
